import BaseRequest from './BaseRequest';
import HTTPRequest from '../HTTPRequest';

export const CUSTOMER_REGISTRATION_BLOCK_CODE = 'customer_registration_block';

export const CUSTOMER_REGISTRATION_BLOCK_MESSAGE =
    'You are unable to register for this class. Please contact the studio for more information.';

export function formatRegistrationRestriction(error = {}, itemName = '') {
    const description =
        error.code === CUSTOMER_REGISTRATION_BLOCK_CODE
            ? CUSTOMER_REGISTRATION_BLOCK_MESSAGE
            : error.description || 'You are unable to register for this class.';

    return {
        key: error.code || 'registration_restricted',
        field: 'non_field_errors',
        message: itemName ? `${itemName}: ${description}` : description,
    };
}

export function parseRegistrationEligibilityResult(result = {}, options = {}) {
    const {
        itemName = '',
        ignoredGlobalRestrictionCodes = [],
        membershipFilter = () => true,
    } = options;

    const globalRestrictions = Array.isArray(result?.global_restrictions)
        ? result.global_restrictions
        : [];
    const errors = globalRestrictions
        .filter(
            (error) => !ignoredGlobalRestrictionCodes.includes(error?.code),
        )
        .map((error) => formatRegistrationRestriction(error, itemName));

    const membershipOptions = Array.isArray(result?.membership_options)
        ? result.membership_options
        : [];
    const memberships = membershipOptions.filter(membershipFilter);
    const hasResult = Object.keys(result).length > 0;

    return {
        status: hasResult && globalRestrictions.length === 0,
        errors,
        memberships,
        missingFormsUrl: result?.prerequisites?.completion_url ?? '',
        forms: result?.prerequisites?.forms ?? [],
    };
}

export default class RegistrationEligibility extends BaseRequest {
    /**
     * RegistrationEligibility constructor
     * 
     * @param {string} requestMode
     * @param {string} providerId
     * @param {string} customerId
     * @param {boolean} auth
     * @param {boolean} fowardRequest
     */
    constructor(requestMode = 'customer', providerId = '', customerId = '', auth = true, fowardRequest = false) {
        super(requestMode, providerId, customerId, auth, fowardRequest);
        this.setClassName('RegistrationEligibility');
        this.setEndpoint('registration_eligibility');
    }

    /**
     * @param {string} targetType
     * @param {string} customerId
     * @param {string} eventId
     * @param {string} venueId
     * @param {string} checkinDate
     * @param {string} appointmentSlot
     * @return {object}
     */
    async check(targetType = 'class', customerId = '', eventId = '', venueId = '', checkinDate = '', appointmentSlot = '') {
        if (customerId == '') {
            customerId = this.getCustomerId();
        }

        let body = {
            'customer': customerId,
            'target_type': targetType
        }
        if (appointmentSlot != '') {
            body['appointment_slot'] = appointmentSlot;
        }
        if (checkinDate != '') {
            body['checkin_date'] = checkinDate;
        }
        if (eventId != '') {
            body['event'] = eventId;
        }
        if (venueId != '') {
            body['checkin_venue'] = venueId;
        }

        const request = new HTTPRequest(
            'POST',
            `${this.getEndpoint()}/check`,
            this.getHeaders(),
            {},
            body
        );
        if (this.getForwardRequest()) {
            request = this.buildForwardRequest(
                this.getClassName(),
                'check',
                {
                    'targetType': targetType,
                    'customerId': customerId,
                    'eventId': eventId,
                    'venueId': venueId,
                    'checkinDate': checkinDate,
                    'appointmentSlot': appointmentSlot
                }
            );
        }

        return await request.sendRequest();
    }
}
